/**
 * School fact hooks — lightweight factual grounding for the School priority.
 *
 * Why this exists:
 *   Before this module, School priority output was reworded from existing
 *   tags ("families anchoring around schools") and users called it generic.
 *   What families actually care about is concrete signal: what the demand
 *   looks like, whether the area is stronger on primary or secondary, and
 *   whether prices already reflect school demand.
 *
 * Plain-English pass (Phase 6):
 *   User feedback was explicit — the word "catchment" is jargon most people
 *   outside real-estate can't parse. This module now uses plain English
 *   ("Zoned to public schools", "Families choose this area for school
 *   access", "No strong public-school zone advantage"). The internal
 *   `school-catchment` tag on verdicts is a *data-layer* hint only, not a
 *   user-facing string, and is left alone so downstream scoring stays put.
 *
 * Design rules:
 *   - Hand-curated catalog for recognisable marquee suburbs, plus a
 *     tag-based fallback so every suburb gets *some* concrete signal.
 *   - No external APIs, no ranking tables, no live data.
 *   - One short signal per suburb; tone drives persona framing.
 *   - New structured fields (`demand`, `focus`, `priceReflection`) feed
 *     the `schoolLine` helper used by the Top Picks panel and verdict card
 *     for a one-liner of the form:
 *       "Strong secondary-school demand — prices reflect the zones"
 */

import type { Verdict } from "../data/verdicts";

export type SchoolTone = "strong" | "mixed" | "limited" | "emerging";

/** Demand strength — maps from tone but is named for the user's question. */
export type SchoolDemand = "strong" | "mixed" | "weak" | "emerging";

/** Whether the area is known more for primary or secondary school pull. */
export type SchoolFocus = "primary" | "secondary" | "both" | "unknown";

/** Whether local prices reflect school demand or move independently. */
export type SchoolPriceReflection = "reflected" | "mixed" | "independent";

export interface SchoolFact {
  /** Single-line factual signal. Used inline in Best-for + Tradeoff copy. */
  signal: string;
  /**
   * Tone — "strong" = a clear public-school draw; "mixed" = options exist
   * but no dominant pull; "limited" = few local options; "emerging" =
   * growth-corridor primaries in, secondary still forming. Controls which
   * persona framing wraps the signal.
   */
  tone: SchoolTone;
  /** Short form (≤ 6 words) — used in compare-card Why bullets. */
  bullet: string;
  /** Structured grounding fields (optional so the type stays backwards-compatible). */
  focus?: SchoolFocus;
  priceReflection?: SchoolPriceReflection;
}

/* -------------------------------------------------------------------------- */
/*  Hand-curated catalog                                                      */
/* -------------------------------------------------------------------------- */

const CATALOG: Record<string, SchoolFact> = {
  // ---- Hyderabad (marquee / recognisable pockets) ------------------------
  "banjara-hills": {
    signal:
      "HPS-Begumpet feeders within reach — families choose this area for school access",
    tone: "strong",
    bullet: "HPS feeders — families pay for access",
    focus: "both",
    priceReflection: "reflected",
  },
  "jubilee-hills": {
    signal:
      "Private K–12 (Oakridge, ISH) lead the draw — no strong public-school zone advantage",
    tone: "mixed",
    bullet: "Private-school heavy; public thin",
    focus: "both",
    priceReflection: "mixed",
  },
  "gachibowli": {
    signal:
      "International school cluster (Oakridge, Chirec) — tech families move here for access",
    tone: "strong",
    bullet: "Int'l schools draw tech families",
    focus: "both",
    priceReflection: "reflected",
  },
  "madhapur": {
    signal:
      "DPS + Orchids within 3 km; families choose this area for school access",
    tone: "strong",
    bullet: "DPS + Orchids anchor demand",
    focus: "both",
    priceReflection: "reflected",
  },
  "kondapur": {
    signal:
      "Mid-band CBSE feeders (Sri Chaitanya) in reach; families buy here for them",
    tone: "strong",
    bullet: "CBSE feeders — mid-band draw",
    focus: "both",
    priceReflection: "mixed",
  },
  "kukatpally": {
    signal:
      "Heavy coaching culture (Narayana, Sri Chaitanya) — no strong public-school zone advantage",
    tone: "mixed",
    bullet: "Coaching-heavy; no zone premium",
    focus: "secondary",
    priceReflection: "independent",
  },
  "nanakramguda": {
    signal:
      "New-estate primaries open; secondary-school options still forming",
    tone: "emerging",
    bullet: "Primaries in; secondary forming",
    focus: "primary",
    priceReflection: "mixed",
  },
  "kokapet": {
    signal:
      "Int'l feeds (Oakridge/Glendale) drawing families into the estate belt",
    tone: "emerging",
    bullet: "Int'l feeds forming in estates",
    focus: "both",
    priceReflection: "mixed",
  },
  "tellapur": {
    signal:
      "Estate belt; primaries open, secondary-school supply still being built",
    tone: "emerging",
    bullet: "Primaries open; secondary forming",
    focus: "primary",
    priceReflection: "mixed",
  },
  "sainikpuri": {
    signal:
      "Kendriya Vidyalaya + Army Public School — steady public-service family demand",
    tone: "strong",
    bullet: "KV + Army schools anchor demand",
    focus: "both",
    priceReflection: "mixed",
  },
  "nizampet": {
    signal:
      "Playschools plentiful, primaries OK, secondary-school options stretch thin",
    tone: "mixed",
    bullet: "Playschool-heavy; secondary thin",
    focus: "primary",
    priceReflection: "independent",
  },
  "manikonda": {
    signal:
      "Private K–12 mix (Epistemo, Delhi World) — no strong public-school zone advantage",
    tone: "mixed",
    bullet: "Private mix; no public anchor",
    focus: "both",
    priceReflection: "mixed",
  },
  "film-nagar": {
    signal:
      "Private K–12 only; no public-school draw in this pocket",
    tone: "limited",
    bullet: "Private only; no public draw",
    focus: "both",
    priceReflection: "independent",
  },
  "financial-district": {
    signal:
      "Near international schools but adult-heavy — families are rare here",
    tone: "limited",
    bullet: "Adult pocket; school pull minor",
    focus: "both",
    priceReflection: "independent",
  },

  // ---- Melbourne (marquee school zones) ----------------------------------
  "mel-brighton": {
    signal:
      "Brighton Grammar + Firbank nearby; prices reflect school demand",
    tone: "strong",
    bullet: "Brighton Grammar + Firbank nearby",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-toorak": {
    signal:
      "Private-school bus routes (St Catherine's, Geelong Grammar); prices reflect demand",
    tone: "strong",
    bullet: "Private-school bus routes",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-south-yarra": {
    signal:
      "Zoned to Melbourne Girls Grammar + Melbourne Grammar — family demand holds prices",
    tone: "strong",
    bullet: "Melb Girls/Boys Grammar zones",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-balwyn": {
    signal:
      "Zoned to Balwyn HS — families choose this area for school access; prices reflect it",
    tone: "strong",
    bullet: "Balwyn HS — families pay for it",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-glen-waverley": {
    signal:
      "Zoned to Glen Waverley Secondary — the school-driven price premium is well known",
    tone: "strong",
    bullet: "Glen Waverley Secondary zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-bentleigh": {
    signal:
      "Zoned to McKinnon Secondary — families specifically buy here for school access",
    tone: "strong",
    bullet: "McKinnon Sec. — families buy for it",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-box-hill": {
    signal:
      "Box Hill HS + selective-school pull; tuition-heavy, school-driven demand",
    tone: "strong",
    bullet: "Box Hill HS + selective-school pull",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-doncaster": {
    signal:
      "Zoned to East Doncaster Secondary; families pay the school-demand premium",
    tone: "strong",
    bullet: "East Doncaster Secondary zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-mentone": {
    signal:
      "Mentone Grammar + St Bede's within 1 km; private-school pull dominates",
    tone: "strong",
    bullet: "Mentone Grammar + St Bede's",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-kew": {
    signal:
      "MLC + Xavier + Trinity Grammar nearby — private-school cluster is the story",
    tone: "strong",
    bullet: "MLC/Xavier/Trinity cluster",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-hawthorn": {
    signal:
      "Scotch College + MLC nearby; private-school demand carries prices",
    tone: "strong",
    bullet: "Scotch + MLC private schools",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-camberwell": {
    signal:
      "Canterbury + Camberwell schools zoned; families choose this area for school access",
    tone: "strong",
    bullet: "Canterbury / Camberwell schools",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-canterbury": {
    signal:
      "Zoned to Canterbury Primary + Camberwell HS — family demand holds the price floor",
    tone: "strong",
    bullet: "Canterbury P + Camberwell HS",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-malvern": {
    signal:
      "De La Salle + Lauriston zones; leafy, school-premium inner-east pocket",
    tone: "strong",
    bullet: "De La Salle + Lauriston",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-richmond": {
    signal:
      "Richmond West PS + Melbourne Girls' College zoned — family demand steady",
    tone: "strong",
    bullet: "Richmond West PS + MGC zones",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-port-melbourne": {
    signal:
      "Zoned to Albert Park College — families choose this area for it",
    tone: "strong",
    bullet: "Albert Park College zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-albert-park": {
    signal:
      "Zoned to Albert Park College — family resales move fast",
    tone: "strong",
    bullet: "Albert Park College zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-middle-park": {
    signal:
      "Zoned to Middle Park Primary + Albert Park College — tight family demand",
    tone: "strong",
    bullet: "Middle Park P + Albert Park C",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-elwood": {
    signal:
      "Zoned to Elwood Primary — a marquee public-school draw; family resales quick",
    tone: "strong",
    bullet: "Elwood Primary — marquee zone",
    focus: "primary",
    priceReflection: "reflected",
  },
  "mel-caulfield": {
    signal:
      "Leibler Yavneh + Bialik + Shelford — Jewish-community school pocket",
    tone: "strong",
    bullet: "Yavneh/Bialik community schools",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-sandringham": {
    signal:
      "Zoned to Sandringham College — families move here specifically for it",
    tone: "strong",
    bullet: "Sandringham College zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-hampton": {
    signal:
      "Zoned to Sandringham College + Hampton Primary — steady family demand",
    tone: "strong",
    bullet: "Sandringham Coll + Hampton P",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-beaumaris": {
    signal:
      "Zoned to Beaumaris Secondary — newer zone, bayside family draw is strong",
    tone: "strong",
    bullet: "Beaumaris Sec. bayside zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-black-rock": {
    signal:
      "Beaumaris Secondary + Sandringham College overlap; coastal family pocket",
    tone: "strong",
    bullet: "Beaumaris Sec. coastal zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-eltham": {
    signal:
      "Zoned to Eltham HS + Montessori options; leafy family pocket",
    tone: "strong",
    bullet: "Eltham HS + leafy schools",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-ivanhoe": {
    signal:
      "Ivanhoe Grammar + Ivanhoe Girls' Grammar — private schools lead",
    tone: "strong",
    bullet: "Ivanhoe Grammar — private lead",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-heidelberg": {
    signal:
      "Ivanhoe Grammar + Heidelberg PS — established family pocket with mixed options",
    tone: "strong",
    bullet: "Ivanhoe Grammar + Heidelberg PS",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-northcote": {
    signal:
      "Zoned to Northcote HS — families pay for the zone; resales move on it alone",
    tone: "strong",
    bullet: "Northcote HS zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-mount-waverley": {
    signal:
      "Zoned to Mount Waverley Secondary — tuition-heavy, school-driven family demand",
    tone: "strong",
    bullet: "Mount Waverley Sec. zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-blackburn": {
    signal:
      "Zoned to Blackburn HS + Koonung Secondary — strong public-school pull",
    tone: "strong",
    bullet: "Blackburn HS zone",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-burwood": {
    signal:
      "Mount Waverley + Box Hill HS overlap — strong public-school mix",
    tone: "strong",
    bullet: "Mt Waverley / Box Hill HS",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-williamstown": {
    signal:
      "Zoned to Williamstown HS — bayside family pocket with steady demand",
    tone: "strong",
    bullet: "Williamstown HS bayside",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-cbd": {
    signal:
      "No strong public-school zone advantage — families commute to inner-north or bayside schools",
    tone: "limited",
    bullet: "No public zone; kids commute out",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-southbank": {
    signal:
      "No strong public-school zone advantage — adult pocket; families rare here",
    tone: "limited",
    bullet: "No zone; adult pocket",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-docklands": {
    signal:
      "Apartment belt; school access means commuting to Port Melbourne / Yarraville",
    tone: "limited",
    bullet: "School access means commuting out",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-carlton": {
    signal:
      "University-adjacent; families rare, public school access is via University HS only",
    tone: "limited",
    bullet: "Uni-adjacent; thin public options",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-north-melbourne": {
    signal:
      "Primary zones exist; secondary options are thinner, families trend out",
    tone: "mixed",
    bullet: "Primary in; secondary thin",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-fitzroy": {
    signal:
      "Zoned to Fitzroy North Primary; secondary-school options are thinner",
    tone: "mixed",
    bullet: "Fitzroy North Primary zone",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-collingwood": {
    signal:
      "Gentrifying; primary zones improving, secondary still thin",
    tone: "mixed",
    bullet: "Primary rising; secondary thin",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-st-kilda": {
    signal:
      "Mixed bayside primary zones; secondary-school options stretch families out",
    tone: "mixed",
    bullet: "Mixed primary; secondary stretches",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-prahran": {
    signal:
      "Private-school bus network dominates; public schools are average",
    tone: "mixed",
    bullet: "Private bus network — public avg",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-tarneit": {
    signal:
      "Growth-corridor; new primaries opening, secondary-school options still forming",
    tone: "emerging",
    bullet: "New primaries; secondary forming",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-truganina": {
    signal:
      "Estate belt; primaries in, secondary-school demand forming ahead of supply",
    tone: "emerging",
    bullet: "Primaries in; secondary forming",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-point-cook": {
    signal:
      "Established primaries; secondary-school supply still catching up with demand",
    tone: "mixed",
    bullet: "Primaries set; secondary tight",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-wyndham-vale": {
    signal:
      "Growth-corridor; new primaries opening, secondary-school options building",
    tone: "emerging",
    bullet: "New primaries; secondary building",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-hoppers-crossing": {
    signal:
      "Established primary + secondary mix; no strong public-school zone advantage",
    tone: "mixed",
    bullet: "Primary/secondary mix; no premium",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-craigieburn": {
    signal:
      "Estate primaries improving; secondary-school supply still forming",
    tone: "emerging",
    bullet: "Estate primaries; secondary forming",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-roxburgh-park": {
    signal:
      "Growth-corridor; primaries adequate, secondary-school options still stretching",
    tone: "emerging",
    bullet: "Primaries adequate; secondary stretches",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-mickleham": {
    signal:
      "New-estate belt; primaries opening ahead of secondary-school options",
    tone: "emerging",
    bullet: "New-estate primaries open",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-kalkallo": {
    signal:
      "Fresh growth belt; primaries under construction, secondary options years out",
    tone: "emerging",
    bullet: "Fresh estate; primaries opening",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-cranbourne-east": {
    signal:
      "Growth-corridor; new primaries in, secondary-school options building",
    tone: "emerging",
    bullet: "New primaries; secondary building",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-clyde-north": {
    signal:
      "Estate belt; primaries open, secondary-school supply forming ahead of demand",
    tone: "emerging",
    bullet: "Primaries open; secondary forming",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-officer": {
    signal:
      "New estate belt; primary supply catching up, secondary-school options still thin",
    tone: "emerging",
    bullet: "Primaries catching up; secondary thin",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-berwick": {
    signal:
      "Berwick College + established Catholic options — settled family pocket",
    tone: "strong",
    bullet: "Berwick College + Catholic options",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-pakenham": {
    signal:
      "Growth-corridor; primaries plentiful, secondary-school supply varies by pocket",
    tone: "emerging",
    bullet: "Primaries set; secondary varies",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-clyde": {
    signal:
      "Estate belt; primaries opening, secondary-school options still forming",
    tone: "emerging",
    bullet: "Estate primaries; secondary forming",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-cranbourne": {
    signal:
      "Broad primary + secondary mix; no strong public-school zone advantage",
    tone: "mixed",
    bullet: "Primary/secondary mix; no premium",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-melton": {
    signal:
      "Outer western; primaries adequate, secondary-school options spread thin",
    tone: "mixed",
    bullet: "Primaries OK; secondary thin",
    focus: "primary",
    priceReflection: "independent",
  },
  "mel-werribee": {
    signal:
      "Mix of new + established primaries; secondary-school options stretching",
    tone: "mixed",
    bullet: "New + old primaries; secondary thin",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-footscray": {
    signal:
      "Mixed primary zones; families balance school choice with rail access",
    tone: "mixed",
    bullet: "Mixed primary; rail-balanced",
    focus: "primary",
    priceReflection: "mixed",
  },
  "mel-yarraville": {
    signal:
      "Zoned to Footscray City College + local primaries — gentrifying family demand",
    tone: "mixed",
    bullet: "Footscray City Coll zone",
    focus: "both",
    priceReflection: "mixed",
  },
  "mel-eaglemont": {
    signal:
      "Ivanhoe Grammar nearby + leafy primaries — quiet school-premium pocket",
    tone: "strong",
    bullet: "Ivanhoe Grammar + leafy primaries",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-kooyong": {
    signal:
      "Scotch College overlap — tiny, high-premium school pocket",
    tone: "strong",
    bullet: "Scotch College overlap",
    focus: "secondary",
    priceReflection: "reflected",
  },
  "mel-surrey-hills": {
    signal:
      "Zoned to Camberwell HS + Chatham Primary — established family pocket",
    tone: "strong",
    bullet: "Camberwell HS + Chatham P",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-armadale": {
    signal:
      "Lauriston + De La Salle zones — private-school-heavy pocket",
    tone: "strong",
    bullet: "Lauriston + De La Salle",
    focus: "both",
    priceReflection: "reflected",
  },
  "mel-east-melbourne": {
    signal:
      "Inner adult pocket — families rare; school access means commuting out",
    tone: "limited",
    bullet: "Adult pocket; families commute out",
    focus: "both",
    priceReflection: "independent",
  },
  "mel-parkville": {
    signal:
      "University-adjacent; families rare, public school access is University HS only",
    tone: "limited",
    bullet: "Uni-adjacent; University HS only",
    focus: "both",
    priceReflection: "independent",
  },
};

/* -------------------------------------------------------------------------- */
/*  Tag-based fallback                                                        */
/* -------------------------------------------------------------------------- */

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

function reasonBlob(v: Verdict): string {
  return (v.reasons || []).join(" ").toLowerCase();
}

/**
 * When no hand-curated entry exists, derive a tone + generic-but-concrete
 * signal from tags + reason text. The signal still reads like a fact about
 * the area rather than boilerplate — families reading the line should feel
 * "this matches what I'd expect here" rather than "this sounds generic".
 */
function fallbackFromTags(v: Verdict): SchoolFact {
  const t = tagSet(v);
  const reasons = reasonBlob(v);

  // School-catchment / family-tagged pocket → strong. (The tag name is
  // internal; the user-facing text is plain English.)
  if (t.has("school-catchment") || t.has("family")) {
    return {
      signal:
        "Established family pocket — listings reference the local school zones directly",
      tone: "strong",
      bullet: "Established family pocket",
      focus: "both",
      priceReflection: "reflected",
    };
  }
  // Leafy / village / mature-infra → likely settled schools.
  if (t.has("leafy") || t.has("village") || t.has("mature-infra")) {
    return {
      signal:
        "Settled schools in the neighbourhood — no strong public-school zone advantage",
      tone: "mixed",
      bullet: "Settled schools; no premium",
      focus: "both",
      priceReflection: "mixed",
    };
  }
  // Growth-corridor / new-build / estate → emerging.
  if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) {
    return {
      signal:
        "Growth-corridor; new primaries opening, secondary-school options still forming",
      tone: "emerging",
      bullet: "New primaries; secondary forming",
      focus: "primary",
      priceReflection: "independent",
    };
  }
  // CBD / nightlife / bars → limited (school is not the pull here).
  if (t.has("cbd") || t.has("nightlife") || t.has("bars")) {
    return {
      signal:
        "No strong public-school zone advantage — families typically commute kids out",
      tone: "limited",
      bullet: "No zone; commute out",
      focus: "both",
      priceReflection: "independent",
    };
  }
  // IT corridor → often private / international feeds.
  if (t.has("it-corridor")) {
    return {
      signal:
        "Private + international K–12 cluster in reach; public schools mixed",
      tone: "mixed",
      bullet: "Private + int'l cluster in reach",
      focus: "both",
      priceReflection: "mixed",
    };
  }
  // Heritage / premium-inner → private-heavy.
  if (t.has("heritage") || t.has("premium-inner")) {
    return {
      signal:
        "Private-school heavy; public-school pull is a minor driver",
      tone: "mixed",
      bullet: "Private-school heavy",
      focus: "both",
      priceReflection: "mixed",
    };
  }
  // Reason-text fallback: some verdicts already mention schools.
  if (/school|grammar|primary|college|zone/i.test(reasons)) {
    return {
      signal:
        "Local schools are part of the listing story, not the headline",
      tone: "mixed",
      bullet: "Schools in listings, not headline",
      focus: "both",
      priceReflection: "mixed",
    };
  }
  // Default — no strong signal.
  return {
    signal:
      "No standout public-school zone — rely on a street-level school pick",
    tone: "mixed",
    bullet: "No standout public zone",
    focus: "unknown",
    priceReflection: "independent",
  };
}

/* -------------------------------------------------------------------------- */
/*  Public entry                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Return the school fact for a Verdict. Catalog entry wins; otherwise the
 * tag-based fallback keeps the output grounded in the suburb's character.
 */
export function getSchoolFact(v: Verdict): SchoolFact {
  return CATALOG[v.id] ?? fallbackFromTags(v);
}

/** Short bullet (≤ 6 words) — used by compare-card Why bullets. */
export function schoolFactBullet(v: Verdict): string {
  return getSchoolFact(v).bullet;
}

/* -------------------------------------------------------------------------- */
/*  Grounded school line — one user-facing sentence per suburb                */
/* -------------------------------------------------------------------------- */

function demandFromTone(tone: SchoolTone): SchoolDemand {
  if (tone === "strong") return "strong";
  if (tone === "limited") return "weak";
  if (tone === "emerging") return "emerging";
  return "mixed";
}

function focusPhrase(focus: SchoolFocus | undefined): string {
  if (focus === "primary") return "primary-school";
  if (focus === "secondary") return "secondary-school";
  if (focus === "both") return "school";
  return "school";
}

function priceClause(pr: SchoolPriceReflection | undefined): string {
  if (pr === "reflected") return "prices reflect the zones";
  if (pr === "mixed") return "school demand shapes some listings";
  if (pr === "independent") return "prices move independently of school demand";
  return "";
}

/**
 * One-line user-facing summary for the School priority surface.
 *
 * Shape: `<Demand-word> <focus> demand — <price clause>` or a tailored
 * single clause for the `weak`/`emerging` cases where the "prices reflect"
 * framing would read off.
 *
 * Examples:
 *   "Strong secondary-school demand — prices reflect the zones"
 *   "Mixed school options — school demand shapes some listings"
 *   "No strong public-school zone advantage"
 *   "Emerging school area — new primaries open, secondary still forming"
 */
export function schoolLine(v: Verdict): string {
  const fact = getSchoolFact(v);
  const demand = demandFromTone(fact.tone);
  const focus = fact.focus ?? "unknown";
  const pr = fact.priceReflection ?? "mixed";

  if (demand === "weak") {
    return "No strong public-school zone advantage";
  }
  if (demand === "emerging") {
    return "Emerging school area — new primaries open, secondary-school options still forming";
  }
  if (demand === "strong") {
    return `Strong ${focusPhrase(focus)} demand — ${priceClause(pr)}`;
  }
  // mixed
  const base = `Mixed ${focusPhrase(focus)} options`;
  const tail = priceClause(pr);
  return tail ? `${base} — ${tail}` : base;
}
